/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-filename-extension */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Students model used to render the dashboards
 *
 * @file Students.js
 * @author Gladson Samuel S
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import AssignmentHelperUtils from '../../../common/utils/AssignmentHelperUtils';
import AssignmentDetails from './StudentAssignments';
import { Assignment } from './Assignment';
import { AET_STORE_EVENTS, ASSIGNMENT_GET_QUERY } from '../common/Constants';
import {
  getStudentDueDatesRecordFromRosterStudent,
  mergeAssignmentDueDatesIntoRosterStudents
} from '../utils/StudentDueDatesAdjustmentUtils';

const PerformanceSummary = types.model({
  questionsAttempted: types.maybeNull(types.number),
  needsReviewCount: types.maybeNull(types.number),
  gettingThereCount: types.maybeNull(types.number),
  failedFirstAttemptCount: types.maybeNull(types.number),
  manyIncorrectAttemptsCount: types.maybeNull(types.number),
  struggleScore: types.maybeNull(types.number),
  incorrectFirstAttemptRisk: types.maybeNull(types.number),
  multipleIncorrectAttemptsRisk: types.maybeNull(types.number),
  performanceRisk: types.maybeNull(types.number),
  lowConfidenceRisk: types.maybeNull(types.number),
  mediumConfidenceRisk: types.maybeNull(types.number),
  confidenceRisk: types.maybeNull(types.number),
  struggleRisk: types.maybeNull(types.number),
  engagementRisk: types.maybeNull(types.number),
  overallRisk: types.maybeNull(types.number),
  overallStatus: types.maybeNull(types.string)
});


const Student = types.model({
  studentId: types.maybeNull(types.string),
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  avatarUrl: types.maybeNull(types.string),
  initials: types.maybeNull(types.string),
  progressSummary: types.model({
    completed: types.optional(types.maybeNull(types.number), 0),
    pastDueCompleted: types.optional(types.maybeNull(types.number), 0),
    inProgress: types.optional(types.maybeNull(types.number), 0),
    notStarted: types.optional(types.maybeNull(types.number), 0)
  }),
  progress: types.optional(types.array(types.model({
    label: types.maybeNull(types.string),
    value: types.maybeNull(types.number),
    color: types.maybeNull(types.string),
    type: types.maybeNull(types.string)
  })), []),
  assignmentDetails: types.optional(types.array(AssignmentDetails), []),
  performanceSummary: types.optional(types.maybeNull(PerformanceSummary), null),
  overallStatus: types.maybeNull(types.frozen())
});

const EMPTY_PROGRESS = {
  completed: 0,
  pastDueCompleted: 0,
  inProgress: 0,
  notStarted: 0
};

/** Name and avatar fields from the active-students API row. */
const getDisplayFields = (student) => {
  const firstName = student.firstName || '';
  const lastName = student.lastName || '';

  return {
    studentId: student.userId,
    firstName: student.firstName,
    lastName: student.lastName,
    name: `${firstName} ${lastName}`.trim(),
    avatarUrl: student.avatarUrl,
    initials: `${firstName[0] || ''}${lastName[0] || ''}`
  };
};

/**
 * One row for /student-roster.
 * Uses cached progress when available; otherwise starts at zero.
 */
const buildRosterRow = (activeStudent, cachedRow) => {
  const display = getDisplayFields(activeStudent);

  if (!cachedRow) {
    return {
      ...display,
      assignmentDetails: [],
      progressSummary: { ...EMPTY_PROGRESS },
      progress: []
    };
  }

  return {
    ...cachedRow,
    ...display,
    avatarUrl: display.avatarUrl ?? cachedRow.avatarUrl
  };
};

const StudentsRosterModel = types
  .model({
    totalAssignments: types.optional(types.maybeNull(types.number), 0),
    /** Active students shown on /student-roster (rebuilt from cache + active list). */
    students: types.optional(types.array(Student), []),
    /** Progress for all course users from progressSummary; kept when a student is inactivated. */
    progressByStudentId: types.optional(types.map(types.frozen()), {}),
    /** This student's assignment records (title, chapters, dueDate) from GET /assignments?studentId=. */
    studentAssignmentsById: types.optional(types.map(types.array(Assignment)), {}),
    courseId: types.maybeNull(types.string),
    isError: types.optional(types.boolean, false),
    isStudentListFetchError: types.optional(types.boolean, false),
    isPending: types.optional(types.boolean, false),
    progressSummaryErrorStatus: types.maybeNull(types.number),
    studentListErrorStatus: types.maybeNull(types.number)
  })
  .actions(self => ({
    resetStudentsRoster() {
      self.students.clear();
      self.progressByStudentId.clear();
      self.studentAssignmentsById.clear();
      self.courseId = null;
      self.totalAssignments = 0;
      self.isError = false;
      self.isStudentListFetchError = false;
      self.progressSummaryErrorStatus = null;
      self.studentListErrorStatus = null;
    },
    setIsPending(isPending) {
      self.isPending = isPending;
    },
    fetchStudentProgress(courseId, studentId) {
      if (!courseId || !studentId) {
        return;
      }

      Framework.getEventManager().publish(
        AET_STORE_EVENTS.STUDENTS_ROSTER_REQUESTED, { courseId, studentId }
      );
    },
    /**
     * GET /assignments?studentId= — roster due-date merge.
     * applyToCourseAssignments: learner self-fetch also patches the assignment store dueDate.
     */
    fetchStudentAssignmentDueDates(courseId, studentId, { applyToCourseAssignments = false } = {}) {
      if (!courseId || !studentId) {
        return;
      }
      Framework.getEventManager().publish(AET_STORE_EVENTS.ASSIGNMENT_GET_REQUESTED, {
        contextId: courseId,
        studentId,
        applyToCourseAssignments,
        ...ASSIGNMENT_GET_QUERY.STUDENT
      });
    },
    mergeAssignmentDueDatesForStudent(studentId, dueDateByAssignmentId) {
      const merged = mergeAssignmentDueDatesIntoRosterStudents(
        getSnapshot(self.students),
        studentId,
        dueDateByAssignmentId
      );
      if (!merged) {
        return;
      }
      applySnapshot(self.students, merged.updatedStudents);
    },
    setStudentAssignments(studentId, assignments = []) {
      if (!studentId) {
        return;
      }
      self.studentAssignmentsById.set(studentId, assignments);
    },
    fetchStudentsRoster(courseId) {
      if ((self.courseId === courseId) || (self.isPending)) {
        return;
      }
      Framework.getEventManager().publish(AET_STORE_EVENTS.STUDENTS_ROSTER_REQUESTED, { courseId });
    },
    fetchStudents(courseId) {
      Framework.getEventManager().publish(AET_STORE_EVENTS.STUDENTS_ROSTER_REQUESTED, { courseId, isRoster: false });
    },
    setStudentRoster(data) {
      const rendered = AssignmentHelperUtils.renderStudentsList(data);

      // progressSummary includes all users — save every row, then show active only
      self.saveProgressToCache(rendered.students);
      self.totalAssignments = rendered.totalAssignments ?? 0;

      if (rendered.courseId != null) {
        self.courseId = rendered.courseId;
      }

      self.syncWithActiveStudents(Framework.getStoreRegistry().getStore('students'));
    },
    /** Save progressSummary rows keyed by studentId (not removed on inactivate). */
    saveProgressToCache(rows = []) {
      rows.forEach((row) => {
        const id = row.studentId || row.userId;

        if (id) {
          self.progressByStudentId.set(id, { ...row, studentId: id });
        }
      });
    },
    setDefaultStudentsRoster() {
      const students = Framework.getStoreRegistry().getStore('students').studentsList;
      const heroBanner = Framework.getStoreRegistry().getStore('herobanner');
      const book = heroBanner.getHeroBook() || {};
      if (students.length > 0 && book.course_id) {
        const data = {
          courseId: book.course_id,
          totalAssignments: 0,
          students: students.map(student => ({
            studentId: student.userId,
            assignmentDetails: [],
            progressSummary: {
              completed: 0,
              pastDueCompleted: 0,
              inProgress: 0,
              notStarted: 0
            }
          }))
        };
        self.setStudentRoster(data);
      }
    },
    setStudents(students) {
      const studentsList = self.studentsList.map((student) => {
        const studentData = students.findStudentById(student.studentId);
        if (studentData) {
          return {
            ...student,
            firstName: studentData.firstName,
            lastName: studentData.lastName,
            name: `${studentData.firstName} ${studentData.lastName}`,
            avatarUrl: studentData.avatarUrl,
            initials: `${studentData.firstName?.[0] ?? ''}${studentData.lastName?.[0] ?? ''}`
          };
        }

        return student;
      });

      applySnapshot(self.students, studentsList);
      self.saveProgressToCache(studentsList);
    },
    clearStudents() {
      self.students.clear();
      self.courseId = null;
    },
    setIsError(isError) {
      self.isError = isError;
    },
    setIsStudentListFetchError(isStudentListFetchError) {
      self.isStudentListFetchError = isStudentListFetchError;
    },
    setProgressSummaryErrorStatus(status) {
      if (!status) {
        return;
      }

      self.progressSummaryErrorStatus = status;
    },
    setStudentListErrorStatus(status) {
      if (!status) {
        return;
      }

      self.studentListErrorStatus = status;
    },
    /**
     * Rebuild /student-roster list from active students + cached progress.
     * progressSummary has all users; Students.students has active only.
     */
    syncWithActiveStudents(studentsStore) {
      const activeStudents = studentsStore?.studentsList || [];
      const visibleRows = activeStudents.map((activeStudent) => {
        const id = activeStudent.userId;
        const cachedRow = id ? self.progressByStudentId.get(id) : null;

        return buildRosterRow(activeStudent, cachedRow);
      });

      applySnapshot(self.students, visibleRows);
    }
  })).views(self => ({
    get studentsList() {
      return getSnapshot(self.students);
    },
    get studentsListBySorted() {
      const students = [...getSnapshot(self.students)];

      return students.sort((a, b) => a.name?.localeCompare(b.name) || 0).filter(student => !!student.name);
    },
    findStudentById(studentId) {
      return self.studentsList.find(student => student.studentId === studentId);
    },
    /**
     * Get the assignment progress for a student
     * @param {String} studentId
     * @param {String} assignmentId
     * @returns {Object}
     */
    getStudentAssignmentProgress(studentId, assignmentId) {
      if (!studentId || !assignmentId) {
        return null;
      }

      const student = self.findStudentById(studentId);

      if (!student) {
        return null;
      }

      const assignmentProgress = student?.assignmentDetails?.find(
        assignment => assignment.assignmentId === assignmentId
      );

      return assignmentProgress || null;
    },
    /** @returns {Record<string, string>} assignmentId -> due ISO from merged roster row */
    getStudentSpecificDueDatesRecord(studentId) {
      return getStudentDueDatesRecordFromRosterStudent(self.findStudentById(studentId));
    },
    /** Full assignment list for a student from GET /assignments?studentId= (title, chapters, dueDate, etc). */
    getStudentAssignments(studentId) {
      return self.studentAssignmentsById.get(studentId) || [];
    }
  }));

export default StudentsRosterModel;
